import React, { useState } from 'react';
import { Heart, Flag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ReportDialog } from '@/components/ReportDialog';
import { EventDisplay } from '@/components/EventDisplay';
import { useLanguage } from '@/contexts/LanguageContext';
import { useServerTags } from '@/hooks/useServerTags';
import { useNavigate } from 'react-router-dom';
import { getGameCategoryPath } from '@/utils/i18nRouting';

interface ServerCardFooterProps {
  ip: string;
  user: any;
  isFavorite: boolean;
  favoriteMutation: any;
  serverId: string;
  onFavoriteClick: (e: React.MouseEvent) => void;
  category: string;
  tags: string[];
  isServerPrestige?: boolean;
}

export const ServerCardFooter: React.FC<ServerCardFooterProps> = ({
  ip,
  user,
  isFavorite,
  favoriteMutation,
  serverId,
  onFavoriteClick,
  category,
  tags,
  isServerPrestige = false,
}) => {
  const [showReportDialog, setShowReportDialog] = useState(false);
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { data: serverTags } = useServerTags();

  const getLocalizedTagName = (tagName: string) => {
    const tag = serverTags?.find((t) => t.name === tagName);
    return tag?.displayName || tagName;
  };

  const handleCategoryClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const categoryPath = getGameCategoryPath(language, category);
    navigate(categoryPath);
  };

  const showIp =
    ip &&
    ip.trim() &&
    ip !== 'N/A' &&
    ip !== '0.0.0.0' &&
    !ip.includes('set realmlist') &&
    !ip.includes('logon.') &&
    ip.length < 30;

  return (
    <>
      <div className="flex items-center justify-between gap-2 pt-1.5 border-t border-slate-700/40">
        {/* Zone gauche: catégorie + tags */}
        <div className="flex items-center gap-1.5 flex-1 min-w-0 overflow-hidden">
          <Badge
            variant="secondary"
            className="text-[10px] sm:text-xs bg-blue-600/90 text-blue-100 hover:bg-blue-700 cursor-pointer transition-colors flex-shrink-0 px-1.5 py-0.5"
            onClick={handleCategoryClick}
          >
            {category}
          </Badge>

          {/* IP compacte - desktop uniquement */}
          {showIp && (
            <div className="hidden lg:inline-flex items-center px-1.5 py-0.5 bg-slate-800/60 rounded border border-slate-600/40">
              <code className="text-[10px] text-slate-300 font-mono truncate max-w-[120px]">
                {ip}
              </code>
            </div>
          )}

          {/* Tags - limités */}
          <div className="hidden sm:flex gap-1 items-center overflow-hidden">
            {tags.slice(0, 2).map((tag, index) => (
              <Badge
                key={index}
                variant="outline"
                className="text-[10px] text-slate-400 border-slate-600/50 flex-shrink-0 px-1.5 py-0"
              >
                {getLocalizedTagName(tag)}
              </Badge>
            ))}
            {tags.length > 2 && (
              <Badge
                variant="outline"
                className="text-[10px] text-slate-500 border-slate-600/40 flex-shrink-0 px-1 py-0"
              >
                +{tags.length - 2}
              </Badge>
            )}
          </div>

          {/* Tag unique sur mobile */}
          <div className="flex sm:hidden gap-1 items-center overflow-hidden">
            {tags.slice(0, 1).map((tag, index) => (
              <Badge
                key={index}
                variant="outline"
                className="text-[10px] text-slate-400 border-slate-600/50 flex-shrink-0 px-1.5 py-0"
              >
                {getLocalizedTagName(tag)}
              </Badge>
            ))}
            {tags.length > 1 && (
              <Badge
                variant="outline"
                className="text-[10px] text-slate-500 border-slate-600/40 flex-shrink-0 px-1 py-0"
              >
                +{tags.length - 1}
              </Badge>
            )}
          </div>

          {/* Événements */}
          <EventDisplay serverId={serverId} variant="compact" isServerPrestige={isServerPrestige} />
        </div>

        {/* Actions compactes */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {user && (
            <Button
              variant="ghost"
              size="sm"
              className="favorite-button p-1.5 hover:bg-slate-700/60 rounded-full flex-shrink-0 h-7 w-7"
              onClick={onFavoriteClick}
              disabled={favoriteMutation.isPending}
              aria-label={
                isFavorite
                  ? language === 'fr'
                    ? 'Retirer des favoris'
                    : language === 'es'
                    ? 'Eliminar de favoritos'
                    : 'Remove from favorites'
                  : language === 'fr'
                  ? 'Ajouter aux favoris'
                  : language === 'es'
                  ? 'Añadir a favoritos'
                  : 'Add to favorites'
              }
            >
              <Heart
                className={`w-3.5 h-3.5 transition-all duration-200 ${
                  isFavorite ? 'fill-red-500 text-red-500' : 'text-slate-400 hover:text-red-400'
                }`}
                aria-hidden="true"
              />
            </Button>
          )}

          <Button
            variant="ghost"
            size="sm"
            className="p-1.5 hover:bg-slate-700/60 rounded-full flex-shrink-0 h-7 w-7"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowReportDialog(true);
            }}
            aria-label={
              language === 'fr'
                ? 'Signaler ce serveur'
                : language === 'es'
                ? 'Reportar este servidor'
                : 'Report this server'
            }
          >
            <Flag
              className="w-3.5 h-3.5 text-slate-400 hover:text-red-400 transition-colors"
              aria-hidden="true"
            />
          </Button>
        </div>
      </div>

      <ReportDialog
        isOpen={showReportDialog}
        onClose={() => setShowReportDialog(false)}
        serverId={serverId}
      />
    </>
  );
};
